/**
 * Attivita API Queries
 * Centralized data fetching for Activities feature
 */

import { supabase, getCurrentUserId } from '../client';
import type { 
  Attivita, AttivitaInsert, AttivitaUpdate,
  QueryOptions 
} from '@/types';
import { DatabaseTable } from '@/types/database.types';

const TABLE = DatabaseTable.ATTIVITA;

// ============================================================
// GET Queries
// ============================================================

export async function getAttivita(options?: QueryOptions & { userId?: string }) {
  const userId = options?.userId || await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');
  
  let query = supabase
    .from(TABLE)
    .select('*', { count: options?.count })
    .eq('user_id', userId);

  // Apply filters
  if (options?.filters?.eq) {
    Object.entries(options.filters.eq).forEach(([key, value]) => {
      query = query.eq(key, value);
    });
  }

  // Apply ordering
  if (options?.order) {
    query = query.order(options.order.column, { 
      ascending: options.order.ascending ?? true 
    });
  }

  // Apply pagination
  if (options?.limit) {
    query = query.limit(options.limit);
  }
  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
  }

  const { data, error, count } = await query;

  return {
    data: data as Attivita[] | null,
    error,
    count,
  };
}

export async function getAttivitaById(id: string, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('id', id)
    .eq('user_id', uid)
    .single();

  return {
    data: data as Attivita | null,
    error,
  };
}

export async function getAttivitaByStato(stato: Attivita['stato'], options?: QueryOptions) {
  return getAttivita({
    ...options,
    filters: { 
      ...options?.filters,
      eq: { ...options?.filters?.eq, stato } 
    },
  });
}

export async function getAttivitaByPriorita(priorita: Attivita['priorita'], options?: QueryOptions) {
  return getAttivita({
    ...options,
    filters: { 
      ...options?.filters,
      eq: { ...options?.filters?.eq, priorita } 
    },
  });
}

export async function getAttivitaInScadenza(giorni: number = 7) {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');
  
  const today = new Date();
  const future = new Date();
  future.setDate(today.getDate() + giorni);
  
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('user_id', userId)
    .gte('data_scadenza', today.toISOString().split('T')[0])
    .lte('data_scadenza', future.toISOString().split('T')[0])
    .order('data_scadenza', { ascending: true });

  return {
    data: data as Attivita[] | null,
    error,
  };
}

// ============================================================
// POST Queries
// ============================================================

export async function createAttivita(attivita: AttivitaInsert, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(TABLE)
    .insert({ ...attivita, user_id: uid })
    .select()
    .single();

  return {
    data: data as Attivita | null,
    error,
  };
}

// ============================================================
// PATCH Queries
// ============================================================

export async function updateAttivita(id: string, updates: AttivitaUpdate, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(TABLE)
    .update(updates)
    .eq('id', id)
    .eq('user_id', uid)
    .select()
    .single();

  return {
    data: data as Attivita | null,
    error,
  };
}

// ============================================================
// DELETE Queries
// ============================================================

export async function deleteAttivita(id: string, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { error } = await supabase
    .from(TABLE)
    .delete()
    .eq('id', id)
    .eq('user_id', uid);

  return { error };
}

// ============================================================
// Stats & Analytics
// ============================================================

export async function getAttivitaStats(userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(TABLE)
    .select('stato')
    .eq('user_id', uid);

  if (error) return { stats: null, error };

  const stats = {
    daFare: data?.filter(a => a.stato === 'da_fare').length || 0,
    inCorso: data?.filter(a => a.stato === 'in_corso').length || 0,
    completate: data?.filter(a => a.stato === 'completata').length || 0,
    annullate: data?.filter(a => a.stato === 'annullata').length || 0,
    totali: data?.length || 0,
  };

  return { stats, error: null };
}
